import { ApolloError } from '@apollo/client';
import { InputAdornment, Stack } from '@mui/material';
import { SetOptional } from 'type-fest';

import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { Mail01Icon } from '@/components/icons/Mail01Icon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { Callout } from '@/components/notifications/Callout/Callout';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { EmployeeTierSelector } from '@/modules/employees/EmployeeTierSelector';

import { useSendSupportUserRequestMutation } from './graphql/SendSupportUserRequest.generated';
import {
  defaultValues,
  InviteColleagueFormShape,
} from './InviteColleagueModal.constants';
import { mapFormDataToInput } from './InviteColleagueModal.utils';

type InviteColleagueModalProps = SetOptional<FormModalProps, 'heading'>;

function InviteColleague({
  onClose,
  heading = 'Invite colleague',
  ...formModalProps
}: InviteColleagueModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const { control, handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<InviteColleagueFormShape>();

  const [sendSupportUserRequest] = useSendSupportUserRequestMutation({
    onCompleted: () => {
      showFeedback('User request submitted successfully.', {
        variant: 'success',
      });
    },
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (formData) => {
    try {
      await sendSupportUserRequest({
        variables: { input: mapFormDataToInput(formData) },
      });
    } catch (error) {
      const userFacingMessages =
        error instanceof ApolloError ? getUserFacingErrorMessages(error) : [];
      showFeedback(
        userFacingMessages.length > 0
          ? userFacingMessages.join('. ')
          : FeedbackMessages.formSaveError
      );
      reportError('Could not submit support user request', error as Error);
      // Re-throw so react-hook-form marks submission as failed
      // and useSubmitSuccessHandler doesn't close the modal
      throw error;
    }
  });

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Submit request
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <Stack spacing={3}>
        <Callout severity="warning">
          <strong>
            New users require approval from your firm administrator.
          </strong>{' '}
          Your request will be routed to the Luminary support team as soon as it
          is submitted.
        </Callout>
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <ModalFormLayout>
            <FormLayoutRow>
              <FormLayoutItem>
                <Card variant="filled" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <EmployeeTierSelector<InviteColleagueFormShape>
                      control={control}
                      fieldName="accessLevel"
                      label="Access level"
                      required
                    />
                  </Stack>
                </Card>
              </FormLayoutItem>
            </FormLayoutRow>
            <FormLayoutRow>
              <FormLayoutItem width={6}>
                <FormAwareTextInput<InviteColleagueFormShape>
                  control={control}
                  fieldName="firstName"
                  label="First name"
                  required
                />
              </FormLayoutItem>
              <FormLayoutItem width={6}>
                <FormAwareTextInput<InviteColleagueFormShape>
                  control={control}
                  fieldName="lastName"
                  label="Last name"
                  required
                />
              </FormLayoutItem>
            </FormLayoutRow>
            <FormLayoutRow>
              <FormLayoutItem>
                <FormAwareTextInput<InviteColleagueFormShape>
                  control={control}
                  type="email"
                  fieldName="email"
                  label="Email"
                  startAdornment={
                    <InputAdornment position="start">
                      <Mail01Icon size={16} />
                    </InputAdornment>
                  }
                  required
                />
              </FormLayoutItem>
            </FormLayoutRow>
          </ModalFormLayout>
        </form>
      </Stack>
    </FormModal>
  );
}

export function InviteColleagueModal(props: InviteColleagueModalProps) {
  const formMethods = useForm<InviteColleagueFormShape>({ defaultValues });

  return (
    <FormProvider {...formMethods}>
      <InviteColleague {...props} />
    </FormProvider>
  );
}
